import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StarRating } from "@/components/Common";
import { formatINR, initials } from "@/lib/format";
import { Plus, Search, UserRound } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  name: "", family_head: "", dob: "", gender: "", occupation: "", pan: "", ucc_code: "",
  email: "", mobile: "", address: "", city: "", state: "", pincode: "", potential: 3, manager_id: "",
};

export default function Clients() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [managers, setManagers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [bday, setBday] = useState("");
  const [bmonth, setBmonth] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();
  const branch = searchParams.get("branch");
  const manager = searchParams.get("manager");
  const product = searchParams.get("product");
  const load = useCallback(() => {
    const params = {};
    if (branch) params.branch = branch;
    if (manager) params.manager = manager;
    if (product) params.product = product;
    if (bday) params.birthday = bday;
    if (bmonth) params.birthday_month = bmonth;
    return api.get("/clients", { params }).then((r) => setClients(r.data));
  }, [branch, manager, product, bday, bmonth]);
  useEffect(() => {
    load();
    if (user.role !== "manager") api.get("/managers").then((r) => setManagers(r.data));
  }, [load, user.role]);

  const create = async () => {
    try {
      await api.post("/clients", form);
      toast.success("Client onboarded");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const filtered = clients.filter((c) =>
    (c.name + (c.mobile || "") + (c.pan || "")).toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="animate-fade-up">
      <PageHeader title="Clients" subtitle={`${clients.length} clients in your scope`}>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-client-button" className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">
              <Plus className="h-4 w-4" /> Onboard Client
            </button>
          </DialogTrigger>
          <DialogContent className="card-surface max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Onboard New Client</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" testid="client-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Field label="Family Head" testid="client-familyhead-input" value={form.family_head} onChange={(v) => setForm({ ...form, family_head: v })} />
              <Field label="Date of Birth" type="date" testid="client-dob-input" value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} />
              <div>
                <label className="text-sm font-medium">Gender</label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="client-gender-select" className="mt-1 bg-secondary border-input"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Occupation" testid="client-occupation-input" value={form.occupation} onChange={(v) => setForm({ ...form, occupation: v })} />
              <Field label="PAN" testid="client-pan-input" value={form.pan} onChange={(v) => setForm({ ...form, pan: v })} />
              <Field label="UCC Code" testid="client-ucc-input" value={form.ucc_code} onChange={(v) => setForm({ ...form, ucc_code: v })} />
              <Field label="Email" testid="client-email-input" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Field label="Mobile" testid="client-mobile-input" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} />
              <Field label="City" testid="client-city-input" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
              <Field label="State" testid="client-state-input" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
              <Field label="Pincode" testid="client-pincode-input" value={form.pincode} onChange={(v) => setForm({ ...form, pincode: v })} />
              <div className="col-span-2">
                <label className="text-sm font-medium">Client Potential</label>
                <div className="mt-1.5"><StarRating value={form.potential} size={26} onChange={(v) => setForm({ ...form, potential: v })} /></div>
              </div>
              {user.role !== "manager" && (
                <div className="col-span-2">
                  <label className="text-sm font-medium">Assign to Manager</label>
                  <Select value={form.manager_id} onValueChange={(v) => setForm({ ...form, manager_id: v })}>
                    <SelectTrigger data-testid="client-manager-select" className="mt-1 bg-secondary border-input"><SelectValue placeholder="Select manager" /></SelectTrigger>
                    <SelectContent>
                      {managers.map((m) => (
                        <SelectItem key={m.id} value={m.id} data-testid={`manager-option-${m.id}`}>{m.name} · {m.branch_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <button data-testid="client-save-button" onClick={create} className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">Onboard Client</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {(branch || manager || product) && (
        <button onClick={() => setSearchParams({})} data-testid="clear-filter-button" className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/15 text-primary px-3 py-1.5 text-xs font-medium row-hover hover:bg-primary/25">
          Filtered{product ? ` · ${product.replace(/_/g, " ")}` : ""} · Clear ✕
        </button>
      )}
      <div className="flex flex-col sm:flex-row gap-3 mb-4 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="client-search-input"
            placeholder="Search by name, mobile or PAN…"
            className="w-full rounded-lg bg-secondary border border-input pl-10 pr-3 py-2.5 text-sm outline-none focus:border-primary transition-colors"
          />
        </div>
        <Select value={bday || "none"} onValueChange={(v) => { setBday(v === "none" ? "" : v); if (v !== "none") setBmonth(""); }}>
          <SelectTrigger data-testid="client-birthday-filter" className="w-full sm:w-48 bg-secondary border-input"><SelectValue placeholder="🎂 Birthday" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">🎂 All Birthdays</SelectItem>
            <SelectItem value="today">Birthday Today</SelectItem>
            <SelectItem value="upcoming">Upcoming (30 days)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={bmonth || "none"} onValueChange={(v) => { setBmonth(v === "none" ? "" : v); if (v !== "none") setBday(""); }}>
          <SelectTrigger data-testid="client-birthday-month-filter" className="w-full sm:w-44 bg-secondary border-input"><SelectValue placeholder="Birthday Month" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Any Month</SelectItem>
            {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
              <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="card-surface rounded-xl overflow-hidden" data-testid="clients-table">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
          <div className="col-span-4">Client</div>
          <div className="col-span-2">Potential</div>
          <div className="col-span-2 hidden sm:block">Manager</div>
          <div className="col-span-2 text-center">Products</div>
          <div className="col-span-2 text-right">Total AUM</div>
        </div>
        {filtered.map((c) => (
          <div
            key={c.id}
            onClick={() => navigate(`/clients/${c.id}`)}
            data-testid={`client-row-${c.id}`}
            className="grid grid-cols-12 gap-3 px-5 py-3.5 items-center cursor-pointer row-hover hover:bg-secondary border-b border-border/40 last:border-0"
          >
            <div className="col-span-4 flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-semibold num shrink-0">
                {initials(c.name)}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{c.name}</div>
                <div className="text-xs text-muted-foreground truncate">{c.mobile || c.email || "—"}</div>
              </div>
            </div>
            <div className="col-span-2"><StarRating value={c.potential} size={13} /></div>
            <div className="col-span-2 hidden sm:block text-sm text-muted-foreground truncate">{c.manager_name}</div>
            <div className="col-span-2 text-center"><span className="num text-sm font-medium">{c.product_count}</span><span className="text-xs text-muted-foreground">/11</span></div>
            <div className="col-span-2 text-right num text-sm font-semibold text-primary">{formatINR(c.total_assets)}</div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="py-16 text-center text-muted-foreground">
            <UserRound className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No clients found.
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input
        type={type}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary transition-colors"
      />
    </div>
  );
}
